import { EventType } from '../../types/event';

const DAY_MS = 86400000;

const startOfDay = (d: Date) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

/** Hány nap van a mai naptól (negatív = a múltban). */
export const dayOffset = (value: string | Date): number =>
  Math.round((startOfDay(new Date(value)).getTime() - startOfDay(new Date()).getTime()) / DAY_MS);

/** "Ma", "Holnap", "Tegnap", különben "Szerda, okt. 14.". */
export const dayLabel = (value: string | Date): string => {
  const offset = dayOffset(value);
  if (offset === 0) return 'Ma';
  if (offset === 1) return 'Holnap';
  if (offset === -1) return 'Tegnap';
  const date = new Date(value);
  const weekday = date.toLocaleDateString('hu-HU', { weekday: 'long' });
  const day = date.toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' });
  return `${weekday.charAt(0).toUpperCase()}${weekday.slice(1)}, ${day}`;
};

/** "16:00" */
export const timeOf = (value: string): string =>
  new Date(value).toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' });

/** ÉÉÉÉ-HH-NN helyi idő szerint (a backend napra kerekít). */
export const isoDate = (d: Date): string => {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export const addDays = (d: Date, days: number): Date => {
  const x = new Date(d);
  x.setDate(x.getDate() + days);
  return x;
};

export const eventTypeName: Record<EventType, string> = {
  [EventType.Training]: 'Edzés',
  [EventType.Match]: 'Mérkőzés',
  [EventType.Meeting]: 'Megbeszélés',
  [EventType.Other]: 'Program',
};

/** Az eseménytípus színe: a lista bal szélén egy vékony csík, semmi más. */
export const eventTypeAccent: Record<EventType, string> = {
  [EventType.Training]: 'bg-sky-500',
  [EventType.Match]: 'bg-rose-500',
  [EventType.Meeting]: 'bg-violet-500',
  [EventType.Other]: 'bg-gray-400',
};

/** Google Térkép útvonal a címhez vagy a helyszín nevéhez. */
export const mapsUrl = (place?: string | null, address?: string | null): string | null => {
  const query = [place, address].filter(Boolean).join(', ');
  return query ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}` : null;
};

export const formatFt = (amount: number): string => `${Math.round(amount).toLocaleString('hu-HU')} Ft`;

/** Csak a keresztnév: "Oláh Levente" -> "Levente". */
export const firstName = (name: string): string => {
  // Magyar névsorrend: elöl a vezetéknév, utána a keresztnév(ek). Két
  // keresztnévnél az elsőn szólítjuk ("Finián Bálint József" -> "Bálint").
  // Az esetleges "dr." előtag nem számít bele.
  const parts = name
    .trim()
    .split(/\s+/)
    .filter((p) => !/^(dr|ifj|id)\.?$/i.test(p));
  return parts.length > 1 ? parts[1] : parts[0] ?? name;
};

/**
 * Rövid nevek a gyerekekhez: a keresztnév, de ha kettőnek ugyanaz (két
 * Levente), akkor a teljes név, hogy ne keveredjenek.
 */
export const shortNames = (players: { playerId: number; name: string }[]): Record<number, string> => {
  const counts: Record<string, number> = {};
  players.forEach((p) => {
    const first = firstName(p.name);
    counts[first] = (counts[first] ?? 0) + 1;
  });
  return Object.fromEntries(players.map((p) => [p.playerId, counts[firstName(p.name)] > 1 ? p.name : firstName(p.name)]));
};
